import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

function LegalShell({
  eyebrow,
  title,
  updated,
  children,
}: {
  eyebrow: string;
  title: string;
  updated: string;
  children: ReactNode;
}) {
  return (
    <div className="legal-page">
      <article className="legal-page__inner">
        <header>
          <p>{eyebrow}</p>
          <h1>{title}</h1>
          <p className="legal-updated">Last updated {updated}.</p>
        </header>
        {children}
      </article>
    </div>
  );
}

export function TermsPage() {
  return (
    <LegalShell eyebrow="Legal" title="Terms of service" updated="25 September 2026">
      <section>
        <h2>Who we are</h2>
        <p>
          BCKT is an independent storage and log platform. It is not yet a registered company and
          has no VAT number or office address. These terms are the contract for using bckt.io. They
          are governed by the laws of the Netherlands. Courts in Amsterdam have exclusive
          jurisdiction, subject to any mandatory consumer protections and rights to bring
          proceedings elsewhere.
        </p>
        <p>
          Questions: <a href="mailto:hello@bckt.io">hello@bckt.io</a>.
        </p>
      </section>
      <section>
        <h2>The service</h2>
        <p>
          BCKT provides object storage, public and private delivery, an S3-compatible gateway,
          workspace controls, and log ingestion. Objects are stored on BCKT storage nodes in the ISO
          27001-certified Interxion AMS5 facility in Amsterdam. Plans differ by capacity. Every
          customer receives the same capabilities, edge network, S3 API, and support quality.
        </p>
      </section>
      <section>
        <h2>Service availability</h2>
        <p>
          We strive for high uptime and commercially reasonable reliability. BCKT is provided on an
          "as is" and "as available" basis. We do not guarantee uninterrupted or error-free service.
          Temporary outages and planned or emergency maintenance may interrupt access.
        </p>
        <p>
          Plans do not include contractual service-level agreements (SLAs), service credits, or
          financial compensation for downtime or maintenance windows. This does not affect rights or
          remedies that applicable law does not allow us to exclude.
        </p>
      </section>
      <section>
        <h2>Eligibility</h2>
        <p>
          You must be at least 16 years old to create an account and use the free workspace. You
          must be 18 years or older to buy a paid plan. Accounts under 18 cannot purchase paid
          plans. By signing in, registering, or continuing with Discord you confirm that, and you
          agree to these Terms, the <Link to="/privacy">Privacy policy</Link>, and the{' '}
          <Link to="/acceptable-use">Acceptable use policy</Link>.
        </p>
      </section>
      <section>
        <h2>Accounts</h2>
        <p>
          Use a permanent email address you control. One free workspace is available per network
          each year. Extra accounts on the same network, Discord account, or device start unpaid
          until a paid plan is active. VPN, proxy, Tor, relay, hosting, disposable email, and banned
          networks cannot create or use a free workspace.
        </p>
        <p>
          You are responsible for every upload, API key, S3 credential, and invite issued from your
          account. If you delete the account, the workspace and its files are removed at once.
          Invited members lose access. Their own BCKT accounts stay in place.
        </p>
      </section>
      <section>
        <h2>Acceptable use</h2>
        <p>
          What you may store, share, or send through BCKT is set out in the{' '}
          <Link to="/acceptable-use">Acceptable use policy</Link>. Child sexual abuse material is
          treated as a criminal emergency: the account is banned, evidence is retained, and a report
          is sent to the National Center for Missing &amp; Exploited Children.
        </p>
      </section>
      <section>
        <h2>Sensitive data and backups</h2>
        <p>
          All objects stored at rest in our European facilities are secured with AES-256 encryption.
          This storage encryption does not prevent access through a public file or a valid shared
          URL. BCKT is not intended for storing unencrypted confidential or sensitive information,
          or mission-critical business secrets.
        </p>
        <p>
          If you upload database backups, sensitive records, or critical archives, encrypt the files
          client-side before uploading and keep the decryption keys separate from BCKT. This helps
          protect their contents if you accidentally make a file public or share a 15-minute
          presigned URL. A URL expiring does not remove copies already downloaded.
        </p>
        <p>
          You remain solely responsible for maintaining and testing independent backups outside of
          BCKT. Do not use BCKT as your only copy. To the fullest extent permitted by applicable
          law, BCKT is not liable for data loss or corruption, subject to the mandatory exceptions
          below.
        </p>
      </section>
      <section>
        <h2>Files and deletion</h2>
        <p>
          You can delete objects from the dashboard, API, or S3 gateway. Public copies are purged
          from cache when technically possible. Account deletion revokes access immediately. We then
          remove stored files and logs and cancel the subscription without a refund. Cleanup
          failures are retried, and we email you when complete. The workspace cannot be recovered.
          Child-safety evidence already preserved for legal reporting is not destroyed.
        </p>
      </section>
      <section>
        <h2>Bandwidth and Fair Use</h2>
        <p>
          Bandwidth is unmetered across all plans. Delivery requests are subject to our{' '}
          <Link to="/fair-use">Fair Use Policy</Link>. If a workspace exceeds its monthly delivery
          request guideline, a 7-day grace period applies. We never bill surprise overage fees. If
          unaddressed after the grace period, new uploads are paused while existing files continue
          to be served. A global CDN rate limit of 60 requests per 10 seconds per IP address
          protects the network.
        </p>
      </section>
      <section>
        <h2>Billing</h2>
        <p>
          Paid plans are billed by Stripe. Capacity changes when Stripe confirms the subscription.
          There are no refunds, except where BCKT made a billing error or support decides otherwise.
          Deleting the account or cancelling a subscription does not create a refund right for the
          current period.
        </p>
      </section>
      <section>
        <h2>Limitation of liability</h2>
        <p>
          To the fullest extent permitted by Dutch law, BCKT and its operators are not liable for
          indirect, consequential, special, incidental, or punitive damages, including lost profits,
          lost revenue, lost donations, business interruption, or downtime arising from your use of,
          or inability to use, the service.
        </p>
        <p>
          Our total aggregate liability, including that of our operators, for all claims arising
          from the same event or related events is limited to the greater of EUR 50 or the amount
          you actually paid BCKT in the one (1) month immediately preceding the event giving rise to
          liability. Related events are treated as one event, dated when the first occurred. This
          limit applies regardless of the legal basis of the claim.
        </p>
        <p>
          These exclusions and limits do not apply to fraud, intentional misconduct, deliberate
          recklessness, or any liability that cannot lawfully be excluded or limited. Nothing in
          these terms removes mandatory consumer rights.
        </p>
      </section>
      <section>
        <h2>Indemnification</h2>
        <p>
          To the extent permitted by applicable law, you must indemnify and hold harmless BCKT and
          its operators against third-party claims, legal disputes, damages, liabilities, and
          reasonable legal costs arising from your uploads or use of the service, including
          copyright infringement and violations of other third-party rights or these terms.
        </p>
        <p>
          We will promptly notify you of a claim and reasonably cooperate in its defence. You may
          not agree to a settlement that imposes obligations on us without our written consent. This
          obligation does not cover claims to the extent caused by our own unlawful conduct and does
          not override mandatory consumer protections.
        </p>
      </section>
      <section>
        <h2>Changes</h2>
        <p>
          We provide at least 30 days of prior notice via email and dashboard notification before
          any material changes take effect regarding pricing, storage limits, or core terms. Minor,
          operational, or legal compliance updates may take effect immediately upon posting to this
          page. Continued use of the service following the 30-day notice period constitutes
          acceptance of the updated terms.
        </p>
      </section>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell eyebrow="Legal" title="Privacy policy" updated="16 September 2026">
      <section>
        <h2>We're sick of corporate bullshit.</h2>
        <p>
          We do not sell customer data. We do not use your files, logs, or account records to train
          models, build advertising profiles, or rent lists. The data exists to run your workspace,
          bill a plan if you buy one, stop abuse, and meet child-safety law.
        </p>
      </section>
      <section>
        <h2>Who this covers</h2>
        <p>
          This policy applies to people in the EU, the UK, the United States, and anywhere else the
          service is used. The data controller is BCKT (bckt.io), operated by its founder and
          technical management team in the Netherlands. BCKT is not yet a registered company. Our
          single official contact for general, technical, and privacy requests is{' '}
          <a href="mailto:hello@bckt.io">hello@bckt.io</a>. Dutch law applies, without limiting your
          mandatory data protection rights or your right to complain to a supervisory authority.
        </p>
      </section>
      <section>
        <h2>What we collect</h2>
        <p>
          Account data: email, display name, optional Discord identifier, password hash or Discord
          link, and two-factor secrets if you enable them. Usage data: files you upload, log events
          you send, workspace memberships, and Stripe billing identifiers. Security data: IP
          addresses, user agents, and anonymous device signals used to stop multi-accounting. Safety
          data: hashes, reports, preserved evidence, and network identifiers tied to child-safety
          incidents.
        </p>
        <p>
          Affiliate data: full legal name, payout country, commission records, and payout details.
          We securely store bank IBAN and SWIFT details in encrypted form, or your PayPal email,
          solely to process commission payouts, including the records required by law.
        </p>
      </section>
      <section>
        <h2>Why we process it</h2>
        <p>
          Contractual necessity (Article 6(1)(b) GDPR): to deliver storage, CDN caching, and log
          search, manage your account, and process subscription billing and affiliate payouts.
        </p>
        <p>
          Legitimate interests (Article 6(1)(f) GDPR): to enforce rate limits, detect bot attacks,
          prevent multi-accounting, and secure our infrastructure. We balance these interests
          against your rights and limit the data used to what is necessary.
        </p>
        <p>
          Child protection: we detect, preserve, and report child sexual abuse material (CSAM) to
          NCMEC and competent authorities. We rely on Article 6(1)(c) where an applicable EU or
          national legal obligation requires processing, and Article 6(1)(e) only where applicable
          law entrusts us with a public-interest task. Protecting children does not itself create a
          blanket public-interest exemption from the GDPR.
        </p>
      </section>
      <section>
        <h2>Cookies</h2>
        <p>
          BCKT only sets cookies that are required to sign in, keep a session, verify email, reset a
          password, or complete two-factor authentication. There is no advertising or analytics
          cookie layer. Strictly necessary cookies support the service you request and do not rely
          on consent obtained merely by registering or signing in.
        </p>
      </section>
      <section>
        <h2>Processors</h2>
        <p>
          Email delivery uses Amazon SES. Payments use Stripe. The edge network uses Cloudflare.
          Signup network checks use FindIP. Object storage and the control plane stay on BCKT nodes
          in Amsterdam. Staff alerting tools are not customer processors.
        </p>
      </section>
      <section>
        <h2>How long we keep it</h2>
        <p>
          Unverified accounts are removed after 7 days. Account deletion is self-service from
          Settings. Once confirmed, account access is revoked immediately. Files and logs are
          removed from active storage in the background, with retries if a system is unavailable. We
          send one final email when cleanup and subscription cancellation are complete. Backup
          copies, where applicable, expire under their retention schedule. Copies already downloaded
          by others cannot be recalled. You can export your account record before deleting.
        </p>
        <p>
          Legal evidence is separate. Under Article 17(3)(b) and (e) GDPR, erasure does not apply to
          the extent retention is necessary to meet an applicable legal obligation or establish,
          exercise, or defend legal claims. This can include preserved CSAM evidence, banned hashes,
          and forensic telemetry frozen for NCMEC and police reporting. Deleting an account does not
          delete that evidence from the isolated vault while the applicable retention grounds
          remain. These exceptions do not permit indefinite retention merely because a report was
          made.
        </p>
      </section>
      <section>
        <h2>Your choices</h2>
        <p>
          You can request access to inspect your personal data, rectify inaccuracies, restrict
          processing, or object to processing where the GDPR provides these rights. You can update
          your profile, export your account record, revoke sessions, or delete your account directly
          from Settings. You also have the right to data portability where applicable. Deletion
          requires an email code and, if 2FA is on, an authenticator or recovery code.
        </p>
        <p>
          Contact <a href="mailto:hello@bckt.io">hello@bckt.io</a> to exercise your rights or ask
          for help. We normally respond within one month. If a lawful extension is needed, we will
          tell you within that month and explain why.
        </p>
        <p>
          You may lodge a complaint with the Dutch Data Protection Authority,{' '}
          <a href="https://autoriteitpersoonsgegevens.nl/en">Autoriteit Persoonsgegevens</a>, or
          another competent supervisory authority. You do not have to contact us first.
        </p>
      </section>
    </LegalShell>
  );
}

export function AcceptableUsePage() {
  return (
    <LegalShell eyebrow="Legal" title="Acceptable use" updated="6 September 2026">
      <section>
        <h2>Scope</h2>
        <p>
          This policy applies to every use of BCKT: the dashboard, API, S3 gateway, public CDN, and
          private objects. It applies to every person and every credential that touches the service.
          The <Link to="/terms">Terms of service</Link> incorporate this page.
        </p>
      </section>
      <section>
        <h2>Child sexual abuse material</h2>
        <p>
          CSAM is treated as a criminal emergency, not a terms dispute. Automated hash matching can
          ban the account at once. Evidence is preserved without a time limit and reported to the
          National Center for Missing &amp; Exploited Children. There is no public appeal. You may
          write to <a href="mailto:hello@bckt.io">hello@bckt.io</a>. If staff says the decision is
          final, it is final.
        </p>
      </section>
      <section>
        <h2>Sexual content</h2>
        <p>
          Ordinary game clips, fiction, and documentary material are allowed. Literal pornographic
          videos, copyrighted adult productions, and any sexual content involving a person who is or
          appears to be a minor are not.
        </p>
      </section>
      <section>
        <h2>Violence and other media</h2>
        <p>
          Nudity, fetish, gore, or extreme violence is allowed when it is ordinary game footage,
          fiction, or documentary context. It is not a licence to host criminal recordings.
        </p>
      </section>
      <section>
        <h2>Copyright</h2>
        <p>
          We remove public CDN objects on a valid DMCA notice, and we can also take down obvious
          public copies of films, games, or other commercial works without waiting. Private storage
          of copyrighted material is allowed as private backup. It must not be shared on the public
          CDN.
        </p>
        <p>
          Game-server assets, including FiveM and similar community files, follow the same copyright
          rule. They may stay on the public CDN until a DMCA notice arrives, because they are harder
          to recognise as third-party work at a glance.
        </p>
      </section>
      <section>
        <h2>Leaks and personal data</h2>
        <p>
          Source-code dumps, leaked databases, and similar copyrighted archives may be stored as
          private objects. They must not be served on the public CDN. Malware, stealers, exploits,
          phishing kits, mining, botnets, command-and-control, and network scanning are forbidden in
          every form.
        </p>
      </section>
      <section>
        <h2>The network</h2>
        <p>
          If traffic is clearly intended to harm the network, we cut it. We email the account. If
          there is no reply within 48 hours, the account is banned and the subscription is cancelled
          with no refund.
        </p>
      </section>
      <section>
        <h2>How we look at files</h2>
        <p>
          We do not browse private objects. A matching banned hash is handled automatically by the
          safety pipeline. If a file was public on the CDN, was reported, and was then made private,
          staff may still review that object.
        </p>
      </section>
      <section>
        <h2>Enforcement</h2>
        <p>
          We cut access immediately according to severity, then email the account before a ban
          proceeds, except for CSAM and other criminal emergencies. You may write to support to
          discuss a sanction. If staff says the decision is final, it is final.
        </p>
      </section>
      <section>
        <h2>Changes</h2>
        <p>
          This policy can change without prior notice. When we can, a notice appears in the
          dashboard. Continued use after a change is acceptance of the new text.
        </p>
      </section>
    </LegalShell>
  );
}

export function FairUsePage() {
  return (
    <LegalShell eyebrow="Policy" title="Fair use policy" updated="11 September 2026">
      <section>
        <h2>Bandwidth & Delivery Request Guidelines</h2>
        <p>
          Bandwidth on BCKT is 100% unmetered and free across all plans. Delivery requests are
          unlimited under our fair use guidelines, which are designed to support active gaming
          communities, web applications, and forums without unexpected overage bills.
        </p>
      </section>

      <section>
        <h2>Monthly Fair Use Request Guidelines</h2>
        <ul>
          <li>
            <strong>Free (10 GB CDN):</strong> Up to 500,000 requests / month
          </li>
          <li>
            <strong>Lite (250 GB CDN):</strong> Up to 2,500,000 requests / month
          </li>
          <li>
            <strong>Starter (500 GB CDN):</strong> Up to 6,000,000 requests / month
          </li>
          <li>
            <strong>Community Pro (1 TB CDN):</strong> Up to 20,000,000 requests / month
          </li>
          <li>
            <strong>Pro (2 TB CDN):</strong> Up to 30,000,000 requests / month
          </li>
          <li>
            <strong>Titan (5 TB CDN):</strong> Up to 75,000,000 requests / month
          </li>
          <li>
            <strong>Network Titan (4 TB CDN):</strong> Up to 80,000,000 requests / month
          </li>
          <li>
            <strong>Monster (10 TB CDN):</strong> Up to 150,000,000 requests / month
          </li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          Log Observatory searches, queries, and event ingestion are 100% unlimited and not metered
          by request counts on any plan.
        </p>
      </section>

      <section>
        <h2>Are you asking yourself: "Wait, so this isn't truly unlimited?"</h2>
        <p>
          Let's be completely honest: nothing in cloud hosting is 100% infinite. We give you the
          most generous capacity we possibly can at the fairest price, but fair use guidelines exist
          to prevent abuse and ensure a single broken script or automated scraping loop doesn't
          degrade performance for everyone else.
        </p>
        <p>
          If you are running a real game server, active forum, or normal application, you will never
          have a problem.
        </p>
      </section>

      <section>
        <h2>What happens if you exceed these guidelines?</h2>
        <p>
          We will <strong>NEVER</strong> automatically charge unexpected overage fees to your credit
          card.
        </p>
        <p>If your workspace reaches 100% of its monthly request guidelines:</p>
        <ol
          style={{
            paddingLeft: '20px',
            marginTop: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <li>
            <strong>Automated Notification:</strong> You will receive an email alert letting you
            know that your server has exceeded normal tier usage.
          </li>
          <li>
            <strong>7-Day Grace Period:</strong> Your service will continue running 100% normally
            for 7 days. This gives you time to inspect your scripts, optimize queries, contact our
            support team, or upgrade your plan.
          </li>
          <li>
            <strong>Upload Pause (Only if unresolved):</strong> If the notice is unaddressed after
            the 7-day grace window, new uploads will be temporarily paused until you upgrade your
            plan or your billing cycle resets. All your existing files will continue to be delivered
            and served normally without interruption.
          </li>
        </ol>
      </section>

      <section>
        <h2>Edge Rate Limiting</h2>
        <p>
          To safeguard against distributed denial of service and misconfigured infinite loops, the
          public CDN enforces an edge rate limit of{' '}
          <strong>60 requests per 10 seconds per IP address</strong>. Normal browsers and game
          clients will not trigger this threshold during legitimate play or navigation.
        </p>
      </section>

      <section>
        <h2>Need more requests without additional storage?</h2>
        <p>
          If your server has a large player base with high request volume or needs custom quotas,
          you can configure and subscribe to your exact plan directly using our{' '}
          <Link to="/pricing">
            <strong>Custom Plan Builder</strong>
          </Link>{' '}
          or inside your workspace{' '}
          <Link to="/dashboard/billing">
            <strong>Billing settings</strong>
          </Link>
          . You can also contact us at{' '}
          <a href="mailto:hello@bckt.io">
            <strong>hello@bckt.io</strong>
          </a>{' '}
          if you have unique infrastructure requirements.
        </p>
      </section>
    </LegalShell>
  );
}
